// Singleton for managing Rokt instance

const ACCOUNT_ID = '2835341786096105351_cd615db4e85f48379cb3eba27c24f750';

export type RoktEventTypes =
  | 'OFFER_ENGAGEMENT'
  | 'POSITIVE_ENGAGEMENT'
  | 'PLACEMENT_CLOSED'
  | 'PLACEMENT_INTERACTIVE'
  | 'PLACEMENT_READY';

export type RoktAttributes = {
  emailMd5?: string;
  emailSha256?: string;
  userId?: string;
  charityAdded?: boolean;
  crowdfundingAdded?: boolean;
  wishlistAdded?: boolean;
  isPremium?: boolean;
};

const state = {
  instance: null as any,
  isInitialized: false,
  hasOffers: false,
};

const createLauncher = async () =>
  window?.Rokt.createLauncher({
    accountId: ACCOUNT_ID,
  });

const initialize = async (pageIdentifier: string, attributes: RoktAttributes): Promise<void> => {
  if (state.isInitialized) {
    return;
  }

  try {
    const launcher = await createLauncher();
    const cd = window.client_data;

    const selection = await launcher.selectPlacements({
      attributes: {
        // Recommended
        emailmd5: attributes.emailMd5,
        emailsha256: attributes.emailSha256,
        zipcode: cd.user_zip_code,
        city: cd.geo_city,
        state: cd.geo_region,
        country: cd.geo_country,
        // Miscellaneous
        conversiontype: cd.event_type,
        clientcustomerid: attributes.userId,
        eventid: cd.event_id,
        eventcity: cd.event_city,
        eventstate: cd.event_state,
        eventzipcode: cd.event_zip,
        eventdate: cd.event_day ? cd.event_day.replace(/-/g, '') : null,
        distance: cd.event_guest_distance,
        userstatus: cd.event_step, // H or G for host or guest
        daysaway: cd.event_days_until,
        charityAdded: attributes.charityAdded,
        crowdfundingAdded: attributes.crowdfundingAdded,
        wishlistAdded: attributes.wishlistAdded,
        sandbox: window.roktSandbox,
      },
      identifier: `${window.roktSandbox ? 'staging-' : ''}${pageIdentifier}`,
    });

    const placements = await selection.getPlacements();
    state.hasOffers = placements?.length > 0;
    state.instance = selection;
    state.isInitialized = true;
  } catch (error) {
    console.error('Failed to initialize Rokt:', error);
  }
};

const subscribe = (eventName: RoktEventTypes, callback: () => void) => {
  if (!state.instance) {
    return null;
  }
  return state.instance.on(eventName).subscribe(callback);
};

const isReady = (): boolean => state.isInitialized;

const getHasOffers = (): boolean => state.hasOffers;

export const clearRoktInstance = async () => {
  await state.instance?.close();
  state.instance = null;
  state.isInitialized = false;
  state.hasOffers = false;
};

export const RoktJs = {
  initialize,
  subscribe,
  isReady,
  getHasOffers,
  clearRoktInstance,
};
